/**
 * SuperadminEcosystem — Gestione SSO apps registrate (CRUD + launch).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Globe, ExternalLink, RefreshCw, Plus, Pencil, Power,
    X, Save, Trash2, Copy, Check,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY_FORM = {
    app_key: "",
    display_name: "",
    domain: "",
    return_urls: [""],
    logout_url: "",
    contact_email: "",
    icon: "globe",
    accent: "#A855F7",
    category: "external",
    parent_app: "",
    multi_brand_domains: false,
    admin_emails: [""],
    allowed_origins: [""],
    active: true,
};

export default function SuperadminEcosystem() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [launching, setLaunching] = useState(null);
    const [drawerMode, setDrawerMode] = useState(null); // null | "create" | "edit"
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [showInactive, setShowInactive] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/superadmin/mc/ecosystem/apps");
            setApps(data.items || []);
            setError("");
        } catch (e) { setError(formatApiError(e)); }
        setLoading(false);
    }, []);

    useEffect(() => {
        document.title = "Ecosistema · Mission Control";
        reload();
    }, [reload]);

    const launchSSO = async (app) => {
        if (app.placeholder) {
            alert(`${app.display_name} è in placeholder. Aggiorna i return_urls quando avrai il dominio definitivo.`);
            return;
        }
        setLaunching(app.app_key);
        try {
            const cb = (app.return_urls || [])[0];
            if (!cb) { alert("Return URL non configurato per questa app."); setLaunching(null); return; }
            // Chiediamo JSON server-to-server (no redirect HTTP, ottengo il token e apro tab manualmente)
            const { data } = await api.get(
                `/auth/sso/authorize?app_key=${encodeURIComponent(app.app_key)}` +
                `&return_url=${encodeURIComponent(cb)}` +
                `&state=admin-launch-${Date.now()}`,
                { headers: { Accept: "application/json" } }
            );
            if (data?.sso_token) window.open(`${cb}?sso=${data.sso_token}`, "_blank", "noopener");
            else alert("SSO ticket non disponibile");
        } catch (e) { alert(formatApiError(e)); }
        setLaunching(null);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setDrawerMode("create");
    };

    const openEdit = async (appKey) => {
        try {
            const { data } = await api.get(`/superadmin/mc/sso/apps/${appKey}`);
            setForm({
                app_key: data.app_key || "",
                display_name: data.display_name || "",
                domain: data.domain || "",
                return_urls: (data.return_urls && data.return_urls.length) ? data.return_urls : [""],
                logout_url: data.logout_url || "",
                contact_email: data.contact_email || "",
                icon: data.icon || "globe",
                accent: data.accent || "#A855F7",
                category: data.category || "external",
                parent_app: data.parent_app || "",
                multi_brand_domains: !!data.multi_brand_domains,
                admin_emails: (data.admin_emails && data.admin_emails.length) ? data.admin_emails : [""],
                allowed_origins: (data.allowed_origins && data.allowed_origins.length) ? data.allowed_origins : [""],
                active: data.active !== false,
            });
            setDrawerMode("edit");
        } catch (e) { alert(formatApiError(e)); }
    };

    const closeDrawer = () => { setDrawerMode(null); setForm(EMPTY_FORM); };

    const saveForm = async () => {
        // Pulizia liste
        const payload = {
            ...form,
            app_key: form.app_key.trim().toLowerCase(),
            display_name: form.display_name.trim(),
            domain: form.domain.trim().toLowerCase(),
            return_urls: form.return_urls.map(s => s.trim()).filter(Boolean),
            admin_emails: form.admin_emails.map(s => s.trim()).filter(Boolean),
            allowed_origins: form.allowed_origins.map(s => s.trim()).filter(Boolean),
            parent_app: form.parent_app.trim() || null,
        };
        if (!payload.app_key || !payload.display_name || !payload.domain || payload.return_urls.length === 0) {
            alert("Compila app_key, display_name, domain e almeno un return_url");
            return;
        }
        setSaving(true);
        try {
            if (drawerMode === "create") {
                await api.post("/superadmin/mc/sso/apps", payload);
            } else {
                const { app_key: _unused, ...rest } = payload;
                await api.patch(`/superadmin/mc/sso/apps/${form.app_key}`, rest);
            }
            await reload();
            closeDrawer();
        } catch (e) { alert(formatApiError(e)); }
        setSaving(false);
    };

    const deactivate = async (appKey) => {
        if (!window.confirm(`Disattivare "${appKey}"? Potrai riattivarla via PATCH.`)) return;
        try {
            await api.delete(`/superadmin/mc/sso/apps/${appKey}`);
            await reload();
        } catch (e) { alert(formatApiError(e)); }
    };

    const reactivate = async (appKey) => {
        try {
            await api.patch(`/superadmin/mc/sso/apps/${appKey}`, { active: true });
            await reload();
        } catch (e) { alert(formatApiError(e)); }
    };

    const visibleApps = apps.filter(a => showInactive ? true : (a.active !== false));

    return (
        <div className="min-h-screen px-6 py-8" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="superadmin-ecosystem">
            <div className="max-w-5xl mx-auto">
                <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6">
                    <ArrowLeft size={12} /> Mission Control
                </Link>

                <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#10B981,#4F8BFF)" }}>
                            <Globe size={18} color="#fff" />
                        </div>
                        <div>
                            <h1 className="text-white text-[24px]" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>Ecosistema esterno</h1>
                            <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {apps.length} app registrate · SSO JWT RS256
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[11.5px] text-white/55 inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="w-3.5 h-3.5" style={{ accentColor: "#4F8BFF" }} data-testid="toggle-show-inactive" />
                            Mostra disattivate
                        </label>
                        <button type="button" onClick={openCreate}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] text-white"
                            style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                            data-testid="btn-create-sso-app">
                            <Plus size={13} /> Nuova app SSO
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

                <div className="space-y-3">
                    {loading && <div className="text-white/45 text-[12.5px] text-center py-8">Caricamento…</div>}
                    {!loading && visibleApps.length === 0 && <div className="text-white/45 text-[12.5px] text-center py-8">Nessuna app registrata. Clicca &quot;Nuova app SSO&quot; per iniziare.</div>}
                    {visibleApps.map((a) => (
                        <div key={a.app_key} className="rounded-2xl p-5 flex items-center gap-4 flex-wrap"
                            style={{ background: "rgba(15,18,28,0.6)", border: `1px solid ${a.active === false ? "rgba(255,255,255,0.04)" : (a.placeholder ? "rgba(255,255,255,0.06)" : a.accent + "40")}`, opacity: a.active === false ? 0.55 : 1 }}
                            data-testid={`eco-row-${a.app_key}`}>
                            <div className="w-11 h-11 rounded-xl grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${a.accent}30, ${a.accent}10)`, border: `1px solid ${a.accent}40` }}>
                                <Globe size={15} style={{ color: a.accent }} />
                            </div>
                            <div className="flex-1 min-w-[260px]">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-white text-[15px]" style={{ fontWeight: 500 }}>{a.display_name}</span>
                                    <Pill mono color="#9EC3FF">{a.app_key}</Pill>
                                    {a.placeholder && <Pill color="#FFD66B">placeholder</Pill>}
                                    {a.multi_brand_domains && <Pill color="#C7A4FF">multi-brand</Pill>}
                                    {a.parent_app && <Pill color="#86EFAC">sub · {a.parent_app}</Pill>}
                                    {a.active === false && <Pill color="#FF7AA3">disattivata</Pill>}
                                </div>
                                <div className="text-white/65 text-[12px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{a.domain}</div>
                                <div className="text-white/40 text-[11px] mt-1 break-all">
                                    return: {(a.return_urls || []).join(" · ")}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-none">
                                <button type="button" onClick={() => openEdit(a.app_key)}
                                    className="p-2 rounded-lg hover:bg-white/[0.08] text-white/70 hover:text-white transition"
                                    title="Modifica" data-testid={`eco-edit-${a.app_key}`}>
                                    <Pencil size={13} />
                                </button>
                                {a.active === false ? (
                                    <button type="button" onClick={() => reactivate(a.app_key)}
                                        className="p-2 rounded-lg hover:bg-white/[0.08] text-[#86EFAC] hover:text-white transition"
                                        title="Riattiva" data-testid={`eco-activate-${a.app_key}`}>
                                        <Power size={13} />
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => deactivate(a.app_key)}
                                        className="p-2 rounded-lg hover:bg-white/[0.08] text-white/55 hover:text-[#FF7AA3] transition"
                                        title="Disattiva" data-testid={`eco-deactivate-${a.app_key}`}>
                                        <Trash2 size={13} />
                                    </button>
                                )}
                                <button type="button" onClick={() => launchSSO(a)} disabled={launching === a.app_key || a.active === false}
                                    className="ml-1 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] text-white transition hover:scale-[1.02] disabled:opacity-50"
                                    style={{ background: a.placeholder ? "rgba(255,255,255,0.06)" : `linear-gradient(135deg, ${a.accent}, ${a.accent}99)`, fontFamily: "Geist, sans-serif", fontWeight: 500 }}
                                    data-testid={`eco-launch-${a.app_key}`}>
                                    {launching === a.app_key ? <RefreshCw size={11} className="animate-spin" /> : <ExternalLink size={11} />}
                                    {a.placeholder ? "In setup" : "Apri"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <HowItWorks />
            </div>

            {drawerMode && (
                <Drawer
                    mode={drawerMode}
                    form={form}
                    setForm={setForm}
                    onClose={closeDrawer}
                    onSave={saveForm}
                    saving={saving}
                />
            )}
        </div>
    );
}

function Pill({ children, color, mono }) {
    return (
        <span className="text-[9.5px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]"
            style={{ background: `${color}22`, color, fontFamily: mono ? "JetBrains Mono, monospace" : "Geist, sans-serif", fontWeight: mono ? 500 : 600 }}>
            {children}
        </span>
    );
}

function HowItWorks() {
    const [copied, setCopied] = useState(false);
    const jwks = `${window.location.origin.replace(/\/$/, "")}/.well-known/jwks.json`;
    const copy = (txt) => {
        navigator.clipboard?.writeText(txt);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div className="mt-8 p-5 rounded-2xl" style={{ background: "rgba(15,18,28,0.4)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="text-white text-[13.5px] mb-1.5" style={{ fontWeight: 500 }}>Come funziona · per il tuo collega che integra come SP</div>
            <div className="text-white/65 text-[12px] leading-relaxed mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                JWT RS256 a vita 60s, nonce anti-replay 10min. JWKS pubblica per la verifica della firma.
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <code className="text-[11.5px] text-[#9EC3FF] flex-1 truncate" style={{ fontFamily: "JetBrains Mono, monospace" }}>{jwks}</code>
                <button type="button" onClick={() => copy(jwks)} className="text-white/55 hover:text-white" data-testid="copy-jwks">
                    {copied ? <Check size={12} className="text-[#86EFAC]" /> : <Copy size={12} />}
                </button>
            </div>
        </div>
    );
}

function Drawer({ mode, form, setForm, onClose, onSave, saving }) {
    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const updList = (key, idx, v) => setForm((f) => {
        const arr = [...(f[key] || [])]; arr[idx] = v; return { ...f, [key]: arr };
    });
    const addList = (key) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), ""] }));
    const rmList = (key, idx) => setForm((f) => {
        const arr = [...(f[key] || [])]; arr.splice(idx, 1);
        return { ...f, [key]: arr.length ? arr : [""] };
    });

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="sso-drawer">
            <div className="w-full max-w-lg h-full overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}
                style={{ background: "#0a0c14", borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            {mode === "create" ? "Nuova" : "Modifica"} · SSO app
                        </div>
                        <h2 className="text-white text-[20px] mt-1" style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>
                            {mode === "create" ? "Registra ecosistema" : form.display_name || "—"}
                        </h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.08] text-white/65 hover:text-white" data-testid="drawer-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="space-y-4">
                    <Row label="App key (slug)" required hint="Lowercase, no spazi. Es: ellymenu, photovendo. Non modificabile dopo creazione.">
                        <input className="form-input" disabled={mode === "edit"} value={form.app_key} onChange={(e) => upd("app_key", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} placeholder="ellymenu" data-testid="form-app-key" />
                    </Row>
                    <Row label="Nome leggibile" required>
                        <input className="form-input" value={form.display_name} onChange={(e) => upd("display_name", e.target.value)} placeholder="EllyMenu" data-testid="form-display-name" />
                    </Row>
                    <Row label="Dominio principale" required hint="Es: ellymenu.it (senza https://)">
                        <input className="form-input" value={form.domain} onChange={(e) => upd("domain", e.target.value)} placeholder="ellymenu.it" data-testid="form-domain" />
                    </Row>

                    <ListField label="Return URL callback" required hint="URL completi su cui rimandare il JWT dopo authorize"
                        values={form.return_urls} onChange={(i, v) => updList("return_urls", i, v)}
                        onAdd={() => addList("return_urls")} onRemove={(i) => rmList("return_urls", i)}
                        placeholder="https://app.ellymenu.it/auth/sso/callback" testid="form-return-urls" />

                    <ListField label="Allowed origins (CORS)" hint="Domini che possono fare CORS verso /api/auth/sso/*"
                        values={form.allowed_origins} onChange={(i, v) => updList("allowed_origins", i, v)}
                        onAdd={() => addList("allowed_origins")} onRemove={(i) => rmList("allowed_origins", i)}
                        placeholder="https://app.ellymenu.it" testid="form-origins" />

                    <ListField label="Email admin / superadmin SP" hint="Utenti TotalKlick che riceveranno ruolo elevato sull'app"
                        values={form.admin_emails} onChange={(i, v) => updList("admin_emails", i, v)}
                        onAdd={() => addList("admin_emails")} onRemove={(i) => rmList("admin_emails", i)}
                        placeholder="andrea@totalklick.it" testid="form-admins" />

                    <Row label="Logout URL (best-effort)">
                        <input className="form-input" value={form.logout_url} onChange={(e) => upd("logout_url", e.target.value)} placeholder="https://app.ellymenu.it/sso/logout" data-testid="form-logout-url" />
                    </Row>
                    <Row label="Contact email (referente tecnico)">
                        <input className="form-input" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} placeholder="tech@ellymenu.it" data-testid="form-contact-email" />
                    </Row>

                    <div className="grid grid-cols-2 gap-3">
                        <Row label="Categoria">
                            <select className="form-input" value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="form-category">
                                <option value="external">External</option>
                                <option value="marketplace">Marketplace</option>
                                <option value="ecommerce">Ecommerce</option>
                                <option value="ho.re.ca">Ho.Re.Ca</option>
                                <option value="service">Service</option>
                                <option value="utility">Utility</option>
                            </select>
                        </Row>
                        <Row label="Accent (hex)">
                            <input className="form-input" value={form.accent} onChange={(e) => upd("accent", e.target.value)} placeholder="#A855F7" data-testid="form-accent" />
                        </Row>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Row label="Parent app (sub-app)" hint="Opzionale: se l'app è figlia di un'altra registrata">
                            <input className="form-input" value={form.parent_app} onChange={(e) => upd("parent_app", e.target.value)} placeholder="offerteincitta" data-testid="form-parent" />
                        </Row>
                        <Row label="Icon">
                            <input className="form-input" value={form.icon} onChange={(e) => upd("icon", e.target.value)} placeholder="globe" data-testid="form-icon" />
                        </Row>
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer rounded-xl p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <input type="checkbox" checked={!!form.multi_brand_domains} onChange={(e) => upd("multi_brand_domains", e.target.checked)} className="mt-0.5 w-4 h-4" style={{ accentColor: "#A855F7" }} data-testid="form-multi-brand" />
                        <span>
                            <span className="block text-white text-[13px]" style={{ fontWeight: 500 }}>Multi-brand domains</span>
                            <span className="block text-white/55 text-[11.5px] mt-0.5">Accetta callback su sotto-domini wildcard (es. <code>*.photovendo.com</code>)</span>
                        </span>
                    </label>

                    {mode === "edit" && (
                        <label className="flex items-start gap-2.5 cursor-pointer rounded-xl p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <input type="checkbox" checked={!!form.active} onChange={(e) => upd("active", e.target.checked)} className="mt-0.5 w-4 h-4" style={{ accentColor: "#86EFAC" }} data-testid="form-active" />
                            <span>
                                <span className="block text-white text-[13px]" style={{ fontWeight: 500 }}>Attiva</span>
                                <span className="block text-white/55 text-[11.5px] mt-0.5">Se disattivata, l&apos;app non riceve più token SSO.</span>
                            </span>
                        </label>
                    )}
                </div>

                <div className="mt-7 flex items-center gap-2 sticky bottom-0 pt-4 pb-1 bg-gradient-to-t from-[#0a0c14] via-[#0a0c14]">
                    <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-full text-[12.5px] text-white/75 hover:text-white hover:bg-white/[0.06] transition" data-testid="drawer-cancel">
                        Annulla
                    </button>
                    <button type="button" onClick={onSave} disabled={saving}
                        className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] text-white disabled:opacity-60"
                        style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                        data-testid="drawer-save">
                        {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {mode === "create" ? "Registra app" : "Salva modifiche"}
                    </button>
                </div>
            </div>

            {/* Style helpers — scoped to drawer */}
            <style>{`
                .form-input {
                    width: 100%;
                    padding: 9px 12px;
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 10px;
                    color: #fff;
                    font-size: 12.5px;
                    font-family: 'JetBrains Mono', monospace;
                    transition: border-color .2s, background .2s;
                }
                .form-input:focus { outline: none; border-color: rgba(168,85,247,0.5); background: rgba(255,255,255,0.05); }
                .form-input:disabled { opacity: 0.55; cursor: not-allowed; }
            `}</style>
        </div>
    );
}

function Row({ label, hint, required, children }) {
    return (
        <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {label} {required && <span className="text-[#FF7A88]">*</span>}
            </label>
            {children}
            {hint && <div className="mt-1 text-[11px] text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>{hint}</div>}
        </div>
    );
}

function ListField({ label, hint, required, values, onChange, onAdd, onRemove, placeholder, testid }) {
    return (
        <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {label} {required && <span className="text-[#FF7A88]">*</span>}
            </label>
            <div className="space-y-1.5">
                {values.map((v, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                        <input className="form-input flex-1" value={v} onChange={(e) => onChange(i, e.target.value)} placeholder={placeholder} data-testid={`${testid}-${i}`} />
                        {values.length > 1 && (
                            <button type="button" onClick={() => onRemove(i)} className="p-2 rounded-lg text-white/45 hover:text-[#FF7AA3] hover:bg-white/[0.06]" data-testid={`${testid}-rm-${i}`}>
                                <X size={13} />
                            </button>
                        )}
                    </div>
                ))}
                <button type="button" onClick={onAdd} className="text-[11.5px] text-white/55 hover:text-white inline-flex items-center gap-1" data-testid={`${testid}-add`}>
                    <Plus size={11} /> Aggiungi
                </button>
            </div>
            {hint && <div className="mt-1 text-[11px] text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>{hint}</div>}
        </div>
    );
}
